/**
 * Validaciones del módulo de soporte
 * 
 * Referencia: sistema-tickets-frontend-COMPLETO.md Sección 7.2
 */

/**
 * Valida el formulario de creación de ticket
 * 
 * @param {Object} formData - Datos del formulario
 * @param {string} formData.tipo - Tipo de ticket (incidencia/solicitud)
 * @param {string} formData.titulo - Título del ticket
 * @param {string} formData.descripcion - Descripción del ticket
 * @param {string} formData.categoriaId - ID de la categoría
 * @param {string} formData.prioridadId - ID de la prioridad
 * @returns {Object} - Objeto con errores (vacío si no hay errores)
 * 
 * @example
 * const errors = validarCrearTicket(formData);
 * if (Object.keys(errors).length > 0) {
 *   // Mostrar errores
 * }
 */
export const validarCrearTicket = (formData) => {
  const errors = {};
  
  // Validar tipo
  if (!formData.tipo) {
    errors.tipo = 'Debe seleccionar un tipo';
  } else if (!['incidencia', 'solicitud'].includes(formData.tipo)) {
    errors.tipo = 'Tipo inválido';
  }
  
  // Validar título
  if (!formData.titulo || !formData.titulo.trim()) {
    errors.titulo = 'El título es obligatorio';
  } else {
    const tituloTrimmed = formData.titulo.trim();
    if (tituloTrimmed.length < 10) {
      errors.titulo = 'El título debe tener al menos 10 caracteres';
    } else if (tituloTrimmed.length > 150) {
      errors.titulo = 'El título no puede exceder 150 caracteres';
    }
  }
  
  // Validar descripción
  if (!formData.descripcion || !formData.descripcion.trim()) {
    errors.descripcion = 'La descripción es obligatoria';
  } else {
    const descripcionTrimmed = formData.descripcion.trim();
    if (descripcionTrimmed.length < 20) {
      errors.descripcion = 'La descripción debe tener al menos 20 caracteres';
    } else if (descripcionTrimmed.length > 1000) {
      errors.descripcion = 'La descripción no puede exceder 1,000 caracteres';
    }
  }
  
  // Validar categoría
  if (!formData.categoriaId) {
    errors.categoriaId = 'Debe seleccionar una categoría';
  }
  
  // Validar prioridad
  if (!formData.prioridadId) {
    errors.prioridadId = 'Debe seleccionar una prioridad';
  }
  
  return errors;
};

/**
 * Valida un mensaje antes de enviarlo
 * 
 * @param {string} content - Contenido del mensaje
 * @param {Array} adjuntos - Array de archivos adjuntos
 * @returns {string|null} - Mensaje de error o null si es válido
 * 
 * @example
 * const error = validarMensaje(content, adjuntos);
 * if (error) {
 *   alert(error);
 * }
 */
export const validarMensaje = (content, adjuntos = []) => {
  const contentTrimmed = (content || '').trim();
  const tieneAdjuntos = adjuntos && adjuntos.length > 0;
  
  // Debe tener contenido o adjuntos
  if (!contentTrimmed && !tieneAdjuntos) {
    return 'Debe escribir un mensaje o adjuntar al menos un archivo';
  }
  
  // Validar longitud máxima
  if (contentTrimmed.length > 10000) {
    return 'El mensaje no puede exceder 10,000 caracteres';
  }
  
  return null;
};

/**
 * Valida un archivo antes de subirlo
 * 
 * @param {File} file - Objeto File del archivo
 * @param {number} maxSizeMB - Tamaño máximo en MB (default: 50)
 * @returns {string|null} - Mensaje de error o null si es válido
 * 
 * @example
 * const error = validarArchivo(file, 50);
 * if (error) {
 *   alert(error);
 * }
 */
export const validarArchivo = (file, maxSizeMB = 50) => {
  if (!file) {
    return 'Archivo no válido';
  }
  
  // Validar tamaño
  const sizeMB = file.size / (1024 * 1024);
  if (sizeMB > maxSizeMB) {
    return `El archivo excede el tamaño máximo de ${maxSizeMB}MB`;
  }
  
  // Tipos MIME permitidos
  const tiposPermitidos = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
    'image/webp',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
    'text/plain',
    'application/zip',
    'application/x-zip-compressed',
    'application/x-rar-compressed',
    'application/rar'
  ];
  
  if (!tiposPermitidos.includes(file.type)) {
    return 'Tipo de archivo no permitido. Formatos permitidos: imágenes, PDF, documentos Office, texto, comprimidos';
  }
  
  return null;
};

/**
 * Valida el formulario de categoría
 * 
 * @param {Object} formData - Datos del formulario
 * @param {string} formData.nombre - Nombre de la categoría
 * @param {string} formData.modulo - Módulo de la categoría
 * @returns {Object} - Objeto con errores (vacío si no hay errores)
 * 
 * @example
 * const errors = validarCategoria(formData);
 */
export const validarCategoria = (formData) => {
  const errors = {};
  
  // Validar nombre
  if (!formData.nombre || !formData.nombre.trim()) {
    errors.nombre = 'El nombre es obligatorio';
  } else {
    const nombreTrimmed = formData.nombre.trim();
    if (nombreTrimmed.length > 100) {
      errors.nombre = 'El nombre no puede exceder 100 caracteres';
    }
  }
  
  // Validar módulo
  if (!formData.modulo || !formData.modulo.trim()) {
    errors.modulo = 'El módulo es obligatorio';
  } else {
    const moduloTrimmed = formData.modulo.trim();
    if (moduloTrimmed.length > 50) {
      errors.modulo = 'El módulo no puede exceder 50 caracteres';
    }
  }
  
  return errors;
};

/**
 * Valida el formulario de prioridad
 * 
 * @param {Object} formData - Datos del formulario
 * @param {string} formData.nombre - Nombre de la prioridad
 * @param {number} formData.firstResponseHours - Horas para primera respuesta
 * @param {number} formData.resolutionHours - Horas para resolución
 * @param {string} formData.color - Color hexadecimal
 * @returns {Object} - Objeto con errores (vacío si no hay errores)
 * 
 * @example
 * const errors = validarPrioridad(formData);
 */
export const validarPrioridad = (formData) => {
  const errors = {};
  
  // Validar nombre
  if (!formData.nombre || !formData.nombre.trim()) {
    errors.nombre = 'El nombre es obligatorio';
  }
  
  // Validar firstResponseHours (puede venir como firstResponseHours o firstResponseHours)
  const firstResponseHours = formData.firstResponseHours || formData.firstResponseHours;
  if (!firstResponseHours || firstResponseHours < 1) {
    errors.firstResponseHours = 'Las horas para primera respuesta deben ser mayor a 0';
  } else if (firstResponseHours > 168) { // Máximo 1 semana
    errors.firstResponseHours = 'Las horas para primera respuesta no pueden exceder 168 horas (1 semana)';
  }
  
  // Validar resolutionHours (puede venir como resolutionHours o resolutionHours)
  const resolutionHours = formData.resolutionHours || formData.resolutionHours;
  if (!resolutionHours || resolutionHours < 1) {
    errors.resolutionHours = 'Las horas para resolución deben ser mayor a 0';
  } else if (resolutionHours > 720) { // Máximo 30 días
    errors.resolutionHours = 'Las horas para resolución no pueden exceder 720 horas (30 días)';
  }
  
  // Validar que resolutionHours sea mayor o igual a firstResponseHours
  if (firstResponseHours && resolutionHours && resolutionHours < firstResponseHours) {
    errors.resolutionHours = 'Las horas para resolución deben ser mayor o igual a las horas para primera respuesta';
  }
  
  // Validar color (opcional)
  if (formData.color) {
    const colorRegex = /^#[0-9A-Fa-f]{6}$/;
    if (!colorRegex.test(formData.color)) {
      errors.color = 'Color debe ser formato hexadecimal válido (ej: #FF0000)';
    }
  }
  
  return errors;
};

/**
 * Valida el formulario de plantilla
 * 
 * @param {Object} formData - Datos del formulario
 * @param {string} formData.nombre - Nombre de la plantilla
 * @param {string} formData.contenido - Contenido de la plantilla
 * @returns {Object} - Objeto con errores (vacío si no hay errores)
 */
export const validarPlantilla = (formData) => {
  const errors = {};
  
  // Validar nombre
  if (!formData.nombre || !formData.nombre.trim()) {
    errors.nombre = 'El nombre es obligatorio';
  } else {
    const nombreTrimmed = formData.nombre.trim();
    if (nombreTrimmed.length > 100) {
      errors.nombre = 'El nombre no puede exceder 100 caracteres';
    }
  }
  
  // Validar contenido
  if (!formData.contenido || !formData.contenido.trim()) {
    errors.contenido = 'El contenido es obligatorio';
  }
  
  return errors;
};

